import {
  Close,
  DescriptionOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  SyncOutlined,
} from '@mui/icons-material';
import { Button } from 'finara-components';

import type { CepModalData } from '@/types/cep';
import { formatCurrency } from '@/utils/formatters';

interface CepModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: CepModalData | null;
}

export const CepModal: React.FC<CepModalProps> = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  const formatDate = (dateNumber: number | null): string => {
    if (!dateNumber) return 'N/A';
    const dateStr = dateNumber.toString();
    // Format: YYYYMMDD -> DD/MM/YYYY
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}/${year}`;
  };

  const getEstadoLabel = (
    estado: string | null
  ): { label: string; color: string; icon: React.ReactNode } => {
    if (!estado)
      return {
        label: 'N/A',
        color: 'text-muted',
        icon: null,
      };

    const estados: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      LQ: {
        label: 'Liquidada',
        color: 'text-green-600',
        icon: <CheckCircleOutlined fontSize="small" className="text-green-600" />,
      },
      D: {
        label: 'Devuelta',
        color: 'text-red-600',
        icon: <CancelOutlined fontSize="small" className="text-red-600" />,
      },
      CCE: {
        label: 'Confirmación enviada',
        color: 'text-green-600',
        icon: <CheckCircleOutlined fontSize="small" className="text-green-600" />,
      },
      CXO: {
        label: 'Por enviar confirma',
        color: 'text-yellow-600',
        icon: <SyncOutlined fontSize="small" className="text-yellow-600" />,
      },
      CCO: {
        label: 'Confirmada',
        color: 'text-green-600',
        icon: <CheckCircleOutlined fontSize="small" className="text-green-600" />,
      },
      CCR: {
        label: 'Confirmación rechazada',
        color: 'text-red-600',
        icon: <CancelOutlined fontSize="small" className="text-red-600" />,
      },
    };

    return (
      estados[estado] || {
        label: estado,
        color: 'text-muted',
        icon: null,
      }
    );
  };

  const estadoInfo = getEstadoLabel(data.estado);

  const handleDownloadCep = () => {
    if (data.urlCEP) {
      window.open(data.urlCEP, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-black opacity-60"
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              onClose();
            }
          }}
          aria-label="Cerrar modal"
        />

        {/* Modal */}
        <div className="relative w-full max-w-3xl mx-auto bg-surface rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-blue-500/10 to-indigo-500/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DescriptionOutlined className="text-blue-600" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-semibold text-brand-white">
                  Comprobante Electrónico de Pago (CEP)
                </h2>
                <p className="text-sm text-muted">Información del comprobante SPEI</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-muted transition-colors rounded-lg hover:bg-white/5"
              aria-label="Cerrar"
            >
              <Close />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Main Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Clave de Rastreo */}
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-sm font-medium text-muted mb-1">Clave de Rastreo</p>
                <p className="text-lg font-semibold text-brand-white font-mono">
                  {data.claveRastreo || 'N/A'}
                </p>
              </div>

              {/* Estado */}
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-sm font-medium text-muted mb-1">Estado</p>
                <div className="flex items-center gap-2">
                  {estadoInfo.icon}
                  <p className={`text-lg font-semibold ${estadoInfo.color}`}>{estadoInfo.label}</p>
                </div>
              </div>

              {/* Fecha de Operación */}
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-sm font-medium text-muted mb-1">Fecha de Operación</p>
                <p className="text-lg font-semibold text-brand-white">
                  {formatDate(data.fechaOperacion)}
                </p>
              </div>

              {/* Monto */}
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-sm font-medium text-muted mb-1">Monto</p>
                <p className="text-lg font-semibold text-brand-white">
                  {data.monto ? formatCurrency(data.monto) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Parties Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Ordenante */}
              <div className="border border-border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-muted mb-3 uppercase">Ordenante</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-muted">Nombre</p>
                    <p className="text-sm font-medium text-brand-white">
                      {data.nombreOrdenante || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Cuenta (CLABE)</p>
                    <p className="text-sm font-mono text-brand-white">
                      {data.cuentaOrdenante || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Beneficiario */}
              <div className="border border-border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-muted mb-3 uppercase">
                  Beneficiario
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-muted">Nombre</p>
                    <p className="text-sm font-medium text-brand-white">
                      {data.nombreBeneficiario || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Cuenta (CLABE)</p>
                    <p className="text-sm font-mono text-brand-white">
                      {data.cuentaBeneficiario || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message if any */}
            {data.message && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted">{data.message}</p>
              </div>
            )}

            {/* CEP Status */}
            {!data.urlCEP && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted">
                  <span className="font-semibold text-brand-white">Nota:</span> El comprobante CEP aún no está
                  disponible. Por favor, inténtelo nuevamente más tarde o espere a que el sistema
                  realice la reconciliación automática.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-white/5">
            <Button variant="white" size="small" onClick={onClose}>
              Cerrar
            </Button>
            {data.urlCEP && (
              <Button
                variant="black"
                size="small"
                onClick={handleDownloadCep}
                startIcon={<DownloadOutlined />}
              >
                Descargar CEP
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
